import React from 'react'
import styled, { useTheme } from 'styled-components/native'
import MaterialCommunityIcon from 'react-native-vector-icons/MaterialCommunityIcons'

import AppointmentCard from 'APP/Components/AppointmentCard'
import NoticeBarContainer from 'APP/Components/NoticeBarContainer'
import Typography from 'APP/Converse/Typography'
import { ResponsiveScrollView } from 'APP/Converse/Layout'

import Analytics from 'APP/Services/Analytics'
import I18n from 'APP/I18n'

// Types
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

const Container = styled.View`
  margin-horizontal: ${({ theme }) => theme.metrics.baseMargin}px;
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 1.5}px;
`

const InnerContainer = styled.View`
  margin-horizontal: ${({ theme }) => theme.metrics.baseMargin}px;
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 2}px;
`

const PrepareTitle = styled(Typography).attrs(() => ({
  variant: 'h3',
}))`
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
`

const PrepareItem = styled.View`
  flex-direction: row;
  padding-vertical: ${({ theme }) => theme.metrics.baseMargin}px;
  border-bottom-color: ${({ theme }) => theme.colors.elementsBorder};
  border-bottom-width: 1px;
`

const PrepareText = styled(Typography).attrs(() => ({
  variant: 'subheader',
}))`
  color: ${({ theme }) => theme.colors.text};
  margin-left: ${({ theme }) => theme.metrics.baseMargin / 1.5}px;
`

const TermsContainer = styled.View`
  margin-top: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
  border-bottom-color: ${({ theme }) => theme.colors.elementsBorder};
  border-bottom-width: 1px;
`

const PrepareTerms = styled(Typography).attrs(() => ({
  variant: 'bodySmall',
}))`
  color: ${({ theme }) => theme.colors.text};
`

const OpenChat = styled(Typography).attrs(() => ({
  variant: 'subheader',
}))`
  color: ${({ theme }) => theme.colors.text};
  margin-top: ${({ theme }) => theme.metrics.baseMargin}px;
`

const OpenChatBold = styled(Typography).attrs(() => ({
  variant: 'subheaderBold',
}))`
  color: ${({ theme }) => theme.colors.text};
  text-decoration-line: underline;
`

type UpcomingAppointmentScreenProps = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  'upcomingAppointment'
>

const UpcomingAppointmentScreen = (props: UpcomingAppointmentScreenProps) => {
  const { navigation, route } = props
  const { appointment } = { ...route?.params, ...props }
  const location = appointment?.encounter_location
  const isVirtual = location === 'virtual'
  const theme = useTheme()

  const openChatText = I18n.t('UpcomingAppointmentScreen.openChat', {
    returnObjects: true,
  }) as string[]

  const handleOpenChatPress = () => {
    Analytics.trackEvent('button_click', {
      button_value: 'UpcomingAppointmentScreen.openChat',
    })

    if (appointment?.episode_id) {
      navigation.navigate('conversation', { channelId: appointment.episode_id })
    }
  }

  const contentByLocation = {
    virtual: {
      prepareList: 'UpcomingAppointmentScreen.prepareList',
      terms: 'UpcomingAppointmentScreen.terms',
      icons: [
        'bell-badge',
        'camera',
        'microphone',
        'map-marker',
        'shield-home',
        'wifi',
        'headphones',
        'card-account-details',
      ],
    },
    on_site: {
      prepareList: 'UpcomingAppointmentScreen.prepareListOnSite',
      terms: 'UpcomingAppointmentScreen.termsOnSite',
      icons: ['clock-check', 'calendar-remove', 'card-account-details', 'face-mask'],
    },
  } as const
  const insets = useSafeAreaInsets()
  const currentContent = isVirtual ? contentByLocation.virtual : contentByLocation.on_site

  return (
    <NoticeBarContainer containerStyle={{ paddingBottom: insets.bottom }}>
      <ResponsiveScrollView>
        <Container>
          <AppointmentCard appointment={appointment} disabled={true} showButtons={true} />
          <InnerContainer>
            <PrepareTitle>{I18n.t('UpcomingAppointmentScreen.prepare')}</PrepareTitle>
            {(I18n.t(currentContent.prepareList, { returnObjects: true }) as string[]).map(
              (text, i) => (
                <PrepareItem key={i}>
                  <MaterialCommunityIcon
                    name={currentContent.icons[i]}
                    size={20}
                    color={theme.colors.accent}
                  />
                  <PrepareText>{text}</PrepareText>
                </PrepareItem>
              )
            )}
            <TermsContainer>
              <PrepareTerms>{I18n.t(currentContent.terms)}</PrepareTerms>
            </TermsContainer>
            {appointment.episode_id && openChatText && (
              <OpenChat>
                {openChatText[0] + ' '}
                <OpenChatBold onPress={handleOpenChatPress} testID={'OPEN_CHAT'}>
                  {openChatText[1]}
                </OpenChatBold>
              </OpenChat>
            )}
          </InnerContainer>
        </Container>
      </ResponsiveScrollView>
    </NoticeBarContainer>
  )
}

export default UpcomingAppointmentScreen
